import React, { useEffect, useState } from 'react'
import { RefreshCw, RotateCcw, Mail } from 'lucide-react'
import {
  DEFAULT_FILTERS,
  describePeriod,
  formatDateTime,
  getFYOptions,
  saveStoredFilters
} from './managementUtils'

const VIEW_TYPES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'ytd', label: 'YTD' }
]

const ManagementFiltersBar = ({
  filters,
  onChange,
  onRefresh,
  lastRefresh,
  completeness,
  emailDisabledNote,
  loading = false,
  compact = false
}) => {
  const [textDraft, setTextDraft] = useState({ customer: filters.customer || '', project: filters.project || '' })

  useEffect(() => {
    setTextDraft({ customer: filters.customer || '', project: filters.project || '' })
  }, [filters.customer, filters.project])

  const update = (patch) => {
    const next = { ...filters, ...patch }
    saveStoredFilters(next)
    onChange(next)
  }

  const commitText = () => {
    if (textDraft.customer !== (filters.customer || '') || textDraft.project !== (filters.project || '')) {
      update(textDraft)
    }
  }

  const reset = () => update({ ...DEFAULT_FILTERS })

  const completenessTone = completeness?.percent == null
    ? 'grey'
    : completeness.percent >= 80 ? 'green' : completeness.percent >= 50 ? 'amber' : 'red'

  return (
    <div className={`mis-filters-bar no-print ${compact ? 'compact' : ''}`}>
      <div className="mis-filters-row">
        <div className="mis-filter">
          <label>View</label>
          <div className="mis-segmented" role="group" aria-label="View type">
            {VIEW_TYPES.map((v) => (
              <button
                key={v.value}
                type="button"
                className={filters.viewType === v.value ? 'active' : ''}
                onClick={() => update({ viewType: v.value })}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
        <div className="mis-filter">
          <label htmlFor="mis-asof">As of</label>
          <input
            id="mis-asof"
            type="date"
            className="form-control form-control-sm"
            value={filters.asOf || ''}
            onChange={(e) => update({ asOf: e.target.value })}
          />
        </div>
        <div className="mis-filter">
          <label htmlFor="mis-fy">FY</label>
          <select
            id="mis-fy"
            className="form-select form-select-sm"
            value={filters.financialYear || ''}
            onChange={(e) => update({ financialYear: e.target.value })}
          >
            <option value="">Current</option>
            {getFYOptions().map((fy) => <option key={fy} value={fy}>FY {fy}</option>)}
          </select>
        </div>
        {!compact && (
          <>
            <div className="mis-filter">
              <label htmlFor="mis-customer">Customer</label>
              <input
                id="mis-customer"
                type="text"
                className="form-control form-control-sm"
                placeholder="Name filter"
                value={textDraft.customer}
                onChange={(e) => setTextDraft((d) => ({ ...d, customer: e.target.value }))}
                onBlur={commitText}
                onKeyDown={(e) => e.key === 'Enter' && commitText()}
              />
            </div>
            <div className="mis-filter">
              <label htmlFor="mis-project">Project</label>
              <input
                id="mis-project"
                type="text"
                className="form-control form-control-sm"
                placeholder="Name filter"
                value={textDraft.project}
                onChange={(e) => setTextDraft((d) => ({ ...d, project: e.target.value }))}
                onBlur={commitText}
                onKeyDown={(e) => e.key === 'Enter' && commitText()}
              />
            </div>
          </>
        )}
        <div className="mis-filter-actions">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={reset} title="Reset filters">
            <RotateCcw size={14} /> Reset
          </button>
          <button type="button" className="btn btn-sm btn-primary" onClick={onRefresh} disabled={loading} title="Refresh data">
            <RefreshCw size={14} className={loading ? 'mis-spin' : ''} /> Refresh
          </button>
        </div>
      </div>
      <div className="mis-filters-meta">
        <span>Period: <strong>{describePeriod(filters.viewType, filters.asOf)}</strong></span>
        <span>Last refresh: <strong>{lastRefresh ? formatDateTime(lastRefresh) : '—'}</strong></span>
        {completeness && (
          <span title={`${completeness.available} of ${completeness.total} metrics have live data`}>
            Data completeness:{' '}
            <span className={`mis-badge mis-badge-${completenessTone}`}>
              {completeness.percent == null ? 'n/a' : `${completeness.percent}%`}
            </span>
          </span>
        )}
        {emailDisabledNote && (
          <span className="mis-muted"><Mail size={12} /> {emailDisabledNote}</span>
        )}
      </div>
    </div>
  )
}

export default ManagementFiltersBar
